import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePedidos } from '@/hooks/usePedidos';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Search, Eye, RefreshCw } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { freteApi } from '@/lib/api';
import type { Pedido } from '@/types';

const statusConfig: Record<string, { label: string; variant: any }> = {
  NOVO: { label: 'Novo', variant: 'secondary' },
  AGUARDANDO_PAGAMENTO: { label: 'Aguard. Pagamento', variant: 'warning' },
  PAGO_CONFIRMADO: { label: 'Pago/Confirmado', variant: 'info' },
  PRODUCAO: { label: 'Em Produção', variant: 'warning' },
  AGUARDANDO_SEPARACAO: { label: 'Pronto p/ Separação', variant: 'success' },
  EM_SEPARACAO: { label: 'Em Separação', variant: 'warning' },
  PRONTO_EXPEDICAO: { label: 'Pronto p/ Entrega', variant: 'success' },
  ENVIADO: { label: 'Enviado', variant: 'info' },
  ENTREGUE: { label: 'Entregue', variant: 'success' },
  CANCELADO: { label: 'Cancelado', variant: 'error' },
};

export function Pedidos() {
  const [status, setStatus] = useState<string>('TODOS');
  const [busca, setBusca] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const { data: pedidos, isLoading, refetch } = usePedidos({ status: status === 'TODOS' ? undefined : status });

  const filteredPedidos = pedidos?.filter((p: Pedido) =>
    busca
      ? p.numeroPedido.toLowerCase().includes(busca.toLowerCase()) ||
        p.cliente?.nome.toLowerCase().includes(busca.toLowerCase())
      : true
  );

  const handleSyncTracking = async () => {
    setIsSyncing(true);
    try {
      await freteApi.syncTracking();
      await refetch();
    } catch (error) {
      console.error('Erro ao sincronizar rastreios:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pedidos</h1>
          <p className="text-gray-500">Gerencie os pedidos dos clientes</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          leftIcon={<RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />}
          onClick={handleSyncTracking}
          disabled={isSyncing}
        >
          {isSyncing ? 'Sincronizando...' : 'Sincronizar Rastreios'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                placeholder="Buscar pedidos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="NOVO">Novo</SelectItem>
                <SelectItem value="PAGO_CONFIRMADO">Confirmado</SelectItem>
                <SelectItem value="PRODUCAO">Em Produção</SelectItem>
                <SelectItem value="AGUARDANDO_SEPARACAO">Pronto p/ Separação</SelectItem>
                <SelectItem value="PRONTO_EXPEDICAO">Pronto p/ Entrega</SelectItem>
                <SelectItem value="ENVIADO">Enviado</SelectItem>
                <SelectItem value="ENTREGUE">Entregue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Nº Pedido</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Cliente</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Origem</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Total</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Data</th>
                    <th className="py-3 text-right text-sm font-medium text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPedidos?.map((pedido: Pedido) => (
                    <tr key={pedido.id} className="border-b border-gray-200/50">
                      <td className="py-4 font-mono text-primary">{pedido.numeroPedido}</td>
                      <td className="py-4 text-gray-900">{pedido.cliente?.nome}</td>
                      <td className="py-4">
                        <Badge variant="secondary">{pedido.origem}</Badge>
                      </td>
                      <td className="py-4 text-gray-900 font-medium">
                        {formatCurrency(pedido.faturamentoTotal)}
                      </td>
                      <td className="py-4">
                        <Badge variant={statusConfig[pedido.status].variant}>
                          {statusConfig[pedido.status].label}
                        </Badge>
                      </td>
                      <td className="py-4 text-gray-500 text-sm">
                        {formatDate(pedido.createdAt)}
                      </td>
                      <td className="py-4 text-right">
                        <Link to={`/admin/pedidos/${pedido.id}`}>
                          <Badge variant="outline" className="cursor-pointer hover:bg-gray-50">
                            <Eye className="h-3 w-3 mr-1" />
                            Ver
                          </Badge>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
